import {useCallback} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {formatPrice, genericMessageHandler} from 'shared/helpers';
import {Event} from 'shared/events/Event';
import {useDialogContext} from 'contexts/DialogContext';
import {useIsConfirmationPage} from 'components/customer/Confirmation/helpers/useIsConfirmationPage';
import {useJobPDFVerificationLink} from 'components/customer/Job/helpers/useJobPDFVerificationLink';
import {useCheckProductVariations} from 'components/customer/Confirmation/helpers/useCheckProductVariations';
import {useNotificationContext} from 'contexts/NotificationContext';

export const useJobPDFVerification = (): Event => {
    const {notify} = useNotificationContext();
    const {isConfirmationPage} = useIsConfirmationPage();
    const {showDialog, hideDialog} = useDialogContext();
    const {userProfile} = useAppContext();
    const {job} = useJobContext() as PartialJob;
    const {pdfLink} = useJobPDFVerificationLink();
    const {checkIfThereAreVariations} = useCheckProductVariations();

    const checkVariations = useCallback(
        async (resolve: (v: boolean) => void) => {
            let jobHasVariations = false;
            let promptMessage = '';

            try {
                jobHasVariations = await checkIfThereAreVariations();
            } catch (e) {
                if (e instanceof Error) {
                    genericMessageHandler(notify, {message: e.message});
                    resolve(false);
                    return;
                }
            }

            if (jobHasVariations) {
                if (job.variationsConfirmed == 0) {
                    promptMessage = `${userProfile.jobSubmitTextWithVariations}`;
                } else {
                    // variation confirmed by manufacturer
                    const variationCost = formatPrice(
                        job.totalVariationCost,
                        job
                    );
                    const variationAcceptedText =
                        userProfile.jobSubmitTextVariationsAccepted.replace(
                            /@variationCost/gi,
                            variationCost
                        );
                    promptMessage = `${variationAcceptedText}`;
                }
            } else {
                promptMessage = `${userProfile.jobSubmitTextNoVariations}`;
            }

            showDialog({
                title: 'Submit Job?',
                message: `<center>${promptMessage}</center>`,
                hideYesButton: true,
                hideNoButton: true,
                buttons: [
                    {
                        show: true,
                        name: 'Yes',
                        controlledHideDialog: true,
                        variant: 'danger',
                        action: () => {
                            resolve(true);
                            hideDialog();
                        },
                    },
                    {
                        show: true,
                        name: 'No',
                        controlledHideDialog: true,
                        action: () => {
                            resolve(false);
                            hideDialog();
                        },
                    },
                ],
            });
        },
        [job, userProfile, notify, checkIfThereAreVariations]
    );

    const displayPrompt = useCallback(
        (resolve: (v: boolean) => void, isConfirmationPage: boolean) => {
            if (isConfirmationPage) {
                void checkVariations(resolve);
                return;
            }

            showDialog({
                title: 'Show PDF',
                message: `<center>Before submitting, please validate the contents of the Job PDF.
                <br/>
                <center>${pdfLink.link}</center>
                <br/>
                Once submitted, you will be unable to make any changes to it.
                <br/>
                <br/>
                By clicking <strong>Yes, I confirm that my ${pdfLink.name} is correct.</strong></center>`,
                hideYesButton: true,
                hideNoButton: true,
                buttons: [
                    {
                        show: true,
                        name: 'Yes',
                        controlledHideDialog: true,
                        variant: 'danger',
                        action: () => {
                            void checkVariations(resolve);
                            hideDialog();
                        },
                    },
                    {
                        show: true,
                        name: 'No',
                        controlledHideDialog: true,
                        action: () => {
                            resolve(false);
                            hideDialog();
                        },
                    },
                ],
            });
        },
        [pdfLink, job, checkVariations]
    );

    const action = useCallback((): Promise<boolean | void> => {
        return new Promise((resolve) => {
            displayPrompt(resolve, isConfirmationPage); // initially display the prompt
        });
    }, [displayPrompt, userProfile, isConfirmationPage]);

    return {
        action,
    };
};
